
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Lock, Plus, AlertCircle, Trash2 } from "lucide-react";
import AddDeviceDialog from "./AddDeviceDialog";
import { Device, useUpdateDevice, useDeleteDevice } from "@/hooks/useDevices";
import { Room } from "@/hooks/useRooms";
import { useState } from "react";
import SmartDevicesSection from "./SmartDevicesSection";
import { useSmartDevices } from "@/hooks/useSmartDevices";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DevicesManagementProps {
  providerId: string;
  devices: Device[];
  devicesLoading: boolean;
  rooms: Room[];
  onDeviceAdded: () => void;
}

const DevicesManagement = ({ 
  providerId, 
  devices, 
  devicesLoading, 
  rooms, 
  onDeviceAdded 
}: DevicesManagementProps) => {
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const updateDevice = useUpdateDevice();
  const deleteDevice = useDeleteDevice();
  const { data: smartDevices, isLoading: smartDevicesLoading } = useSmartDevices(providerId);

  const handleAddDevice = (room: Room) => {
    setSelectedRoom(room);
    setShowAddDialog(true);
  };

  const handleDeviceAdded = () => {
    setShowAddDialog(false);
    setSelectedRoom(null);
    onDeviceAdded();
  };

  const handleToggleDevice = async (deviceId: string, currentEnabled: boolean) => {
    try {
      await updateDevice.mutateAsync({
        id: deviceId,
        is_enabled: !currentEnabled
      });
    } catch (error) {
      console.error('Error toggling device:', error);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await deleteDevice.mutateAsync(deviceId);
      onDeviceAdded(); // Refresh the devices list
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  console.log('DevicesManagement: Rendering with data:', {
    providerId,
    devicesCount: devices?.length || 0,
    roomsCount: rooms?.length || 0,
    rooms: rooms?.map(r => ({ id: r.id, name: r.name }))
  });

  return (
    <div className="space-y-6">
      <SmartDevicesSection
        providerId={providerId}
        devices={smartDevices || []}
        devicesLoading={smartDevicesLoading}
        onDeviceAdded={onDeviceAdded}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <CardTitle className="flex items-center space-x-2">
              <Lock className="h-5 w-5" />
              <span>Access Control Devices</span>
            </CardTitle>
            
            {rooms && rooms.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2">
                {rooms.map((room) => (
                  <Button
                    key={room.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddDevice(room)}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="truncate">Add to {room.name}</span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          {devicesLoading ? (
            <div className="grid grid-cols-1 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Show warning if no rooms */}
              {(!rooms || rooms.length === 0) && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-yellow-800 font-medium">No rooms configured</p>
                    <p className="text-yellow-700 text-sm">You need to create rooms before adding access control devices.</p>
                  </div>
                </div>
              )}

              {/* Show devices if any exist */}
              {devices && devices.length > 0 ? (
                <div className="space-y-4">
                  {devices.map((device) => {
                    const room = rooms?.find(r => r.id === device.room_id);
                    return (
                      <div key={device.id} className="flex flex-col gap-4 p-4 border rounded-lg">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Lock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="font-medium truncate">{device.device_name}</span>
                            <Badge variant={device.seam_device_id ? 'default' : 'secondary'} className="text-xs">
                              {device.seam_device_id ? 'Connected' : 'Local'}
                            </Badge>
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <Badge 
                                  variant={device.is_enabled ? 'default' : 'destructive'} 
                                  className="cursor-help text-xs"
                                >
                                  {device.is_enabled ? 'Active' : 'Disabled'}
                                </Badge>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80">
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold">Device Status</h4>
                                  <p className="text-sm text-gray-600">
                                    {device.is_enabled 
                                      ? "This device is currently active and can be used for customer check-ins."
                                      : "This device is disabled and cannot be used for access control. Customers will not receive access codes for this device."
                                    }
                                  </p>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="break-all">Device ID: {device.device_id}</p>
                            {device.seam_device_id && (
                              <p className="break-all">Seam Device ID: {device.seam_device_id}</p>
                            )}
                            {device.device_pin && (
                              <p>PIN: {device.device_pin}</p>
                            )}
                            {room && (
                              <p className="text-gray-500">Room: {room.name}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`device-${device.id}`}
                              checked={device.is_enabled}
                              onCheckedChange={() => handleToggleDevice(device.id, device.is_enabled)}
                              disabled={updateDevice.isPending}
                            />
                            <label 
                              htmlFor={`device-${device.id}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {device.is_enabled ? 'Enabled' : 'Disabled'}
                            </label>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 w-full sm:w-auto">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove Device
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove Device</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove "{device.device_name}"? This will disable access control for this device and it can be added to other locations.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteDevice(device.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                  disabled={deleteDevice.isPending}
                                >
                                  Remove Device
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Lock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No devices configured yet</p>
                  <p className="text-sm mb-4">Add access control devices to enable self-service entry for customers</p>
                  {rooms && rooms.length > 0 && (
                    <p className="text-sm text-gray-400">Click "Add Device" above to get started</p>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Device Dialog */}
      {selectedRoom && (
        <AddDeviceDialog
          providerId={providerId}
          room={selectedRoom}
          onDeviceAdded={handleDeviceAdded}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
        />
      )}
    </div>
  );
};

export default DevicesManagement;
